/**
 * Calendar background overlays — tint available, closed, past, and day-off slots.
 *
 * FullCalendar paints bg events in the col-bg layer under the slot grid. Slots must be
 * transparent, and `.fc-bg-event` opacity must be forced to 1 (FC default is 0.3).
 */

export const CALENDAR_OVERLAY_COLORS = {
  /** Bookable window within working hours. */
  available: 'rgba(34, 197, 94, 0.08)',
  /** Outside schedule (before open / after close). */
  closed: 'rgba(148, 163, 184, 0.07)',
  /** Past time — grey wash; events also fade via .past-event */
  past: 'rgba(71, 85, 105, 0.16)',
  /** Full day off. */
  dayOff: 'rgba(239, 68, 68, 0.09)',
} as const;

/** Solid swatches for the sidebar legend (rgba washes are hard to preview on white). */
export const CALENDAR_OVERLAY_LEGEND_COLORS = {
  available: '#e8f8ef',
  closed: '#f4f6f8',
  past: '#c8d1dc',
  dayOff: '#fdeaea',
} as const;

/** Fade for events that have already ended. */
export const CALENDAR_PAST_EVENT_STYLE = {
  opacity: 0.35,
  saturate: 0.5,
} as const;

const pastEventFade = `
  opacity: ${CALENDAR_PAST_EVENT_STYLE.opacity} !important;
  filter: saturate(${CALENDAR_PAST_EVENT_STYLE.saturate}) !important;
`;

const overlayBase = `
  border: none !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  background-image: none !important;
  background-clip: border-box !important;
  mix-blend-mode: normal !important;
  opacity: 1 !important;
`;

const availableOverlayBase = `
  border: none !important;
  box-shadow: none !important;
  cursor: default !important;
  pointer-events: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  background-image: none !important;
  background-clip: border-box !important;
  mix-blend-mode: normal !important;
  opacity: 1 !important;
`;

/** Host + tutor schedule calendars (compact week/day grid). */
export const calendarOverlayCompactStyles = `
  /* Slot grid sits above col-bg — must be transparent so overlays show through */
  .fc .fc-timegrid-body .fc-timegrid-slots td.fc-timegrid-slot,
  .fc .fc-timegrid-body .fc-timegrid-slots td.fc-timegrid-slot-minor,
  .fc .fc-timegrid-body .fc-timegrid-slots th.fc-timegrid-axis,
  .fc .fc-timegrid-body .fc-timegrid-slots td.fc-timegrid-slot-lane {
    background-color: transparent !important;
    background: transparent !important;
  }

  /* FC default: bg events at 30% opacity — too faint for schedule shading */
  .fc .fc-bg-event {
    opacity: 1 !important;
  }

  /* Available / bookable working hours */
  .fc .fc-bg-event.working-hours-indicator,
  .fc .fc-event.working-hours-indicator {
    ${availableOverlayBase}
    background-color: ${CALENDAR_OVERLAY_COLORS.available} !important;
    z-index: 0 !important;
  }

  /* Outside working hours */
  .fc .fc-bg-event.blocked-slot.closed-slot:not(.past-slot):not(.day-off),
  .fc .fc-event.blocked-slot.closed-slot:not(.past-slot):not(.day-off) {
    ${overlayBase}
    background-color: ${CALENDAR_OVERLAY_COLORS.closed} !important;
    z-index: 1 !important;
  }

  /* Past time */
  .fc .fc-bg-event.blocked-slot.past-slot:not(.day-off),
  .fc .fc-event.blocked-slot.past-slot:not(.day-off) {
    ${overlayBase}
    background-color: ${CALENDAR_OVERLAY_COLORS.past} !important;
    z-index: 2 !important;
  }

  /* Past events — class applied in eventDidMount (FC timegrid has no fc-day-past) */
  .fc .fc-timegrid-event-harness .fc-event.past-event:not(.fc-bg-event),
  .fc .fc-event.past-event:not(.fc-bg-event) {
    ${pastEventFade}
  }

  /* Day off */
  .fc .fc-bg-event.blocked-slot.day-off,
  .fc .fc-event.blocked-slot.day-off {
    ${overlayBase}
    background-color: ${CALENDAR_OVERLAY_COLORS.dayOff} !important;
    z-index: 3 !important;
  }

  /* Today: no FC yellow wash or custom highlight — same as other days */
  .fc {
    --fc-today-bg-color: transparent;
  }

  .fc .fc-day-today,
  .fc .fc-day-today .fc-col-header-cell,
  .fc .fc-day-today .fc-col-header-cell-cushion,
  .fc .fc-day-today .fc-timegrid-col,
  .fc .fc-day-today .fc-timegrid-col-bg,
  .fc .fc-day-today .fc-timegrid-col-frame,
  .fc .fc-day-today .fc-scrollgrid-sync-inner {
    background-color: transparent !important;
    background-image: none !important;
    box-shadow: none !important;
  }

  /* Hover: don't paint over overlays on unavailable cells */
  .fc .fc-timegrid-slot:has(.fc-bg-event.blocked-slot):hover,
  .fc .fc-timegrid-slot:has(.fc-event.blocked-slot):hover {
    background-color: transparent !important;
  }

  .fc .fc-timegrid-slot:has(.fc-bg-event.blocked-slot),
  .fc .fc-timegrid-slot:has(.fc-event.blocked-slot),
  .fc .fc-timegrid-slot:has(.fc-bg-event.blocked-slot) *,
  .fc .fc-timegrid-slot:has(.fc-event.blocked-slot) * {
    cursor: not-allowed !important;
  }

  /* Available cells: subtle green hover */
  .fc .fc-timegrid-slot:has(.fc-bg-event.working-hours-indicator):not(:has(.fc-bg-event.blocked-slot)):hover,
  .fc .fc-timegrid-slot:has(.fc-event.working-hours-indicator):not(:has(.fc-event.blocked-slot)):hover {
    background-color: rgba(34, 197, 94, 0.12) !important;
  }
`;
